import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Library, Plus, Trash2, FileText, ClipboardCheck, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";

const cat_labels = { invite: "Invite", reminder: "Reminder", rejection: "Rejection", offer: "Offer", followup: "Follow-up", other: "Other" };

export default function ContentLibraryPage() {
  const [tab, setTab] = useState("question-sets");
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link: switch tab based on ?action= param
  useEffect(() => {
    const action = searchParams.get("action");
    if (action === "new-qs") setTab("question-sets");
    else if (action === "new-sc") setTab("scorecards");
    else if (action === "new-email") setTab("emails");
  }, [searchParams]);

  return (
    <div className="space-y-6" data-testid="content-library-page">
      <div>
        <h1 className="text-3xl font-bold font-display flex items-center gap-2">
          <Library className="w-6 h-6 text-pink" /> Content Library
        </h1>
        <p className="text-sm text-neutral-500 mt-1">Reusable question sets, scorecards, and email templates — save once, deploy across every hire.</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-neutral-100">
          <TabsTrigger value="question-sets" data-testid="lib-tab-questions"><FileText className="w-4 h-4 mr-1" /> Question sets</TabsTrigger>
          <TabsTrigger value="scorecards" data-testid="lib-tab-scorecards"><ClipboardCheck className="w-4 h-4 mr-1" /> Scorecards</TabsTrigger>
          <TabsTrigger value="emails" data-testid="lib-tab-emails"><Mail className="w-4 h-4 mr-1" /> Email templates</TabsTrigger>
        </TabsList>
        <TabsContent value="question-sets" className="mt-6"><QuestionSetsTab /></TabsContent>
        <TabsContent value="scorecards" className="mt-6"><ScorecardsTab /></TabsContent>
        <TabsContent value="emails" className="mt-6"><EmailsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function QuestionSetsTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [qs, setQs] = useState([{ prompt: "", think_time: 30, answer_time: 120 }]);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = () => api.get("/library/question-sets").then(r => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (searchParams.get("action") === "new-qs") {
      setOpen(true);
      const p = new URLSearchParams(searchParams); p.delete("action"); setSearchParams(p, { replace: true });
    }
  }, [searchParams]);

  const save = async () => {
    if (!name.trim() || qs.some(x => !x.prompt.trim())) return toast.error("Fill in name + all question prompts");
    try {
      await api.post("/library/question-sets", { name, description: desc, questions: qs });
      toast.success("Question set saved");
      setOpen(false); setName(""); setDesc(""); setQs([{ prompt: "", think_time: 30, answer_time: 120 }]);
      load();
    } catch (e) { toast.error("Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this question set?")) return;
    try { await api.delete(`/library/question-sets/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-neutral-500">{items.length} saved · Applied automatically when creating new jobs</p>
        <Button onClick={() => setOpen(true)} className="btn-primary rounded-full" data-testid="lib-new-qs-btn"><Plus className="w-4 h-4 mr-1" /> New question set</Button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map(qs => (
          <Card key={qs.qs_id} className="border-0 shadow-sm" data-testid={`lib-qs-${qs.qs_id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{qs.name}</h3>
                    {qs.is_default && <Badge variant="outline" className="text-[9px]">Default</Badge>}
                  </div>
                  <p className="text-xs text-neutral-500 mt-1">{qs.description}</p>
                </div>
                {!qs.is_default && (
                  <button onClick={() => del(qs.qs_id)} className="text-neutral-300 hover:text-red-500" data-testid={`lib-qs-del-${qs.qs_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="mt-3 space-y-1.5">
                {qs.questions.map((q, i) => (
                  <div key={i} className="text-xs text-neutral-600 flex gap-2">
                    <span className="text-pink font-mono font-bold">{String(i+1).padStart(2,"0")}</span>
                    <span className="flex-1">{q.prompt}</span>
                    <span className="text-neutral-400 shrink-0">{q.think_time}s / {q.answer_time}s</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="lib-qs-dialog">
          <DialogHeader><DialogTitle>New question set</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input value={name} onChange={e=>setName(e.target.value)} placeholder="Backend Engineer — advanced" data-testid="lib-qs-name" /></div>
            <div><Label className="text-xs">Description</Label><Input value={desc} onChange={e=>setDesc(e.target.value)} data-testid="lib-qs-desc" /></div>
            <div className="space-y-2">
              <Label className="text-xs">Questions</Label>
              {qs.map((q, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <span className="text-pink font-mono text-xs pt-2.5">{String(i+1).padStart(2,"0")}</span>
                  <Textarea value={q.prompt} onChange={e => { const c=[...qs]; c[i].prompt=e.target.value; setQs(c);}} rows={2} className="text-xs" data-testid={`lib-qs-q-${i}`} />
                  <div className="flex flex-col gap-1">
                    <Input type="number" value={q.think_time} onChange={e => { const c=[...qs]; c[i].think_time=+e.target.value; setQs(c);}} className="w-16 text-xs h-8" title="Think seconds" />
                    <Input type="number" value={q.answer_time} onChange={e => { const c=[...qs]; c[i].answer_time=+e.target.value; setQs(c);}} className="w-16 text-xs h-8" title="Answer seconds" />
                  </div>
                  {qs.length > 1 && <button onClick={() => setQs(qs.filter((_,j)=>j!==i))} className="text-red-400 pt-2"><Trash2 className="w-3.5 h-3.5" /></button>}
                </div>
              ))}
              <Button size="sm" variant="ghost" onClick={() => setQs([...qs, { prompt:"", think_time:30, answer_time:120 }])} className="text-xs" data-testid="lib-qs-add-q"><Plus className="w-3 h-3 mr-1" /> Add question</Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={save} data-testid="lib-qs-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ScorecardsTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [traits, setTraits] = useState([{ id:"trait1", name:"", weight:25, description:"" }, { id:"trait2", name:"", weight:25, description:"" }, { id:"trait3", name:"", weight:25, description:"" }, { id:"trait4", name:"", weight:25, description:"" }]);

  const load = () => api.get("/library/scorecards").then(r => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const totalWeight = traits.reduce((s,t)=>s+(+t.weight||0),0);

  const save = async () => {
    if (!name.trim() || traits.some(t => !t.name.trim())) return toast.error("Fill in name and all trait names");
    if (Math.abs(totalWeight-100)>0.5) return toast.error(`Weights must sum to 100 (currently ${totalWeight})`);
    try {
      await api.post("/library/scorecards", { name, description: desc, traits });
      toast.success("Scorecard saved");
      setOpen(false); setName(""); setDesc("");
      load();
    } catch(e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this scorecard?")) return;
    try { await api.delete(`/library/scorecards/${id}`); load(); toast.success("Deleted"); }
    catch(e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-neutral-500">{items.length} saved · Weighted scorecards for evaluators</p>
        <Button onClick={() => setOpen(true)} className="btn-primary rounded-full" data-testid="lib-new-sc-btn"><Plus className="w-4 h-4 mr-1" /> New scorecard</Button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map(sc => (
          <Card key={sc.tpl_id} className="border-0 shadow-sm" data-testid={`lib-sc-${sc.tpl_id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{sc.name}</h3>
                    {sc.is_default && <Badge variant="outline" className="text-[9px]">Default</Badge>}
                  </div>
                  <p className="text-xs text-neutral-500 mt-1">{sc.description}</p>
                </div>
                {!sc.is_default && (
                  <button onClick={() => del(sc.tpl_id)} className="text-neutral-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
              <div className="mt-3 space-y-1.5">
                {sc.traits.map((t,i) => (
                  <div key={t.id} className="text-xs">
                    <div className="flex justify-between"><span className="font-medium">{t.name}</span><span className="text-neutral-500 font-mono">{t.weight}%</span></div>
                    <div className="h-1 bg-neutral-100 rounded-full overflow-hidden mt-0.5">
                      <div className="h-full bg-pink" style={{ width: `${t.weight}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="lib-sc-dialog">
          <DialogHeader><DialogTitle>New scorecard</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input value={name} onChange={e=>setName(e.target.value)} data-testid="lib-sc-name" /></div>
            <div><Label className="text-xs">Description</Label><Input value={desc} onChange={e=>setDesc(e.target.value)} /></div>
            <div>
              <Label className="text-xs flex justify-between">Traits <span className={`font-mono ${Math.abs(totalWeight-100)>0.5 ? "text-red-500":"text-teal"}`}>Sum: {totalWeight}%</span></Label>
              {traits.map((t, i) => (
                <div key={i} className="flex gap-2 mt-2">
                  <Input placeholder="Trait name" value={t.name} onChange={e => { const c=[...traits]; c[i].name=e.target.value; setTraits(c);}} className="flex-1" data-testid={`lib-sc-trait-${i}`} />
                  <Input type="number" value={t.weight} onChange={e => { const c=[...traits]; c[i].weight=+e.target.value; setTraits(c);}} className="w-20" />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={save} data-testid="lib-sc-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmailsTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name:"", subject:"", body:"", category:"invite" });
  const [filter, setFilter] = useState("all");
  const [searchParams, setSearchParams] = useSearchParams();

  const load = () => api.get("/library/email-templates").then(r => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (searchParams.get("action") === "new-email") {
      setOpen(true);
      const p = new URLSearchParams(searchParams); p.delete("action"); setSearchParams(p, { replace: true });
    }
  }, [searchParams]);

  const save = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.body.trim()) return toast.error("Fill in all fields");
    try {
      await api.post("/library/email-templates", form);
      toast.success("Template saved");
      setOpen(false); setForm({ name:"", subject:"", body:"", category:"invite" });
      load();
    } catch(e) { toast.error("Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try { await api.delete(`/library/email-templates/${id}`); load(); toast.success("Deleted"); }
    catch(e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const filtered = filter === "all" ? items : items.filter(x => x.category === filter);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="flex gap-1.5 flex-wrap">
          {["all", ...Object.keys(cat_labels)].map(c => (
            <button key={c} onClick={() => setFilter(c)}
              data-testid={`lib-email-filter-${c}`}
              className={`text-xs px-3 py-1 rounded-full border ${filter===c?"bg-navy text-white border-navy":"bg-white text-neutral-600 border-neutral-200 hover:border-pink"}`}>
              {c === "all" ? "All" : cat_labels[c]}
            </button>
          ))}
        </div>
        <Button onClick={() => setOpen(true)} className="btn-primary rounded-full" data-testid="lib-new-email-btn"><Plus className="w-4 h-4 mr-1" /> New template</Button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {filtered.map(t => (
          <Card key={t.template_id} className="border-0 shadow-sm" data-testid={`lib-email-${t.template_id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold">{t.name}</h3>
                    <Badge variant="outline" className="text-[9px]">{cat_labels[t.category]}</Badge>
                    {t.is_default && <Badge variant="outline" className="text-[9px]">Default</Badge>}
                  </div>
                  <p className="text-xs text-neutral-500 mt-1"><span className="font-semibold">Subj:</span> {t.subject}</p>
                </div>
                {!t.is_default && <button onClick={() => del(t.template_id)} className="text-neutral-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>}
              </div>
              <pre className="text-xs text-neutral-600 mt-3 whitespace-pre-wrap font-sans bg-neutral-50 p-3 rounded max-h-40 overflow-y-auto">{t.body}</pre>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="lib-email-dialog">
          <DialogHeader><DialogTitle>New email template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="lib-email-name" /></div>
            <div><Label className="text-xs">Category</Label>
              <select className="w-full px-3 py-2 text-sm rounded-lg border border-neutral-200" value={form.category} onChange={e=>setForm({...form,category:e.target.value})} data-testid="lib-email-category">
                {Object.entries(cat_labels).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Subject</Label><Input value={form.subject} onChange={e=>setForm({...form,subject:e.target.value})} placeholder="You're invited to interview for {{job_title}}" /></div>
            <div><Label className="text-xs">Body</Label><Textarea rows={7} value={form.body} onChange={e=>setForm({...form,body:e.target.value})} placeholder="Use {{candidate_name}}, {{job_title}}, {{recruiter_name}}, {{invite_link}}…" data-testid="lib-email-body" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={save} data-testid="lib-email-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
